import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import parse from 'html-react-parser';
import clsx from 'clsx';

import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormLabel from '@material-ui/core/FormLabel';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import DoneIcon from '@material-ui/icons/Done';
import Grid from '@material-ui/core/Grid';

import { useStyles } from './styles';
import useUpdateFilterValue from '../../../hooks/useUpdateFilterValue';
import PartCountLabel from '../PartCountLabel/PartCountLabel';

const Chips = ({
	color,
	variant,
	fullWidth,
	multiple,
	hiddenlabel,
	appliedValue,
	updateAppliedValue,
	disabled,
	conditional,
	label,
	longLabel,
	values,
	cid,
	categoryId,
	...rest
}) => {
	const { t } = useTranslation();
	const classes = useStyles({
		color,
		variant,
		fullWidth,
		numberOfChips: values.length
	});

	const { updateFilterValue } = useUpdateFilterValue();
	const appliedState = useSelector(state => state.filters.appliedState);
	const countOfPartsForFilterOptions = useSelector(state => state.filters.countOfPartsForFilterOptions || {});

	const handleSelect = value => () => {
		if (cid) {
			updateFilterValue(cid, categoryId, value);
		} else {
			updateAppliedValue(value);
		}
	};

	const handleSelectMultiple = value => () => {
		const currentIndex = appliedValue.indexOf(value);
		const newValue = [...appliedValue];

		if (currentIndex === -1) {
			newValue.push(value);
		} else {
			newValue.splice(currentIndex, 1);
		}

		updateFilterValue(cid, categoryId, newValue);
	};

	return (
		<FormControl component="fieldset" fullWidth={fullWidth}>
			{hiddenlabel || (
				<FormLabel component="legend" color="primary" className={classes.formLabel}>
					<Grid item>
						{longLabel && (
							<Typography display="inline" variant="body1">
								{parse(longLabel) + '\u00a0\u00a0'}
							</Typography>
						)}

						<Typography display="inline" variant="body1">
							{parse(label)}
						</Typography>
					</Grid>
				</FormLabel>
			)}
			<FormGroup className={classes.formGroup}>
				{values.map((value, index) => {
					const chipValue = conditional ? value.label : value;
					const partCountExist = countOfPartsForFilterOptions[cid] && countOfPartsForFilterOptions[cid][value.label];
					const partCount = partCountExist ? countOfPartsForFilterOptions[cid][value.label].count : 0;
					const displayLabel = conditional ? (
						<>
							{i18next.exists(`filterValue.${value.label}`) ? t(`filterValue.${value.label}`) : value.label}
							{partCountExist && <PartCountLabel count={partCount} />}{' '}
						</>
					) : (
						<>{t(`filterValue.${value}`)}</>
					);
					const isSelected = multiple ? appliedValue.indexOf(chipValue) !== -1 : appliedValue === chipValue;
					const isChipActive = isSelected && appliedState[cid]?.defaultValue.indexOf(chipValue) === -1;
					const disableByPartcount = partCountExist && partCount === 0;

					return (
						<Chip
							key={chipValue + '-' + index + '-chip'}
							avatar={multiple && isSelected && <DoneIcon />}
							label={displayLabel}
							onClick={multiple ? handleSelectMultiple(chipValue) : handleSelect(chipValue)}
							disabled={!isChipActive && (disabled || disableByPartcount)}
							{...rest}
							className={clsx(classes.chip, {
								[classes.selectedChip]: isSelected && !isChipActive,
								[classes.activeChip]: isChipActive,
								[classes.selectedChipMultiple]: multiple && isSelected && !isChipActive
							})}
							tabIndex={0}
						/>
					);
				})}
			</FormGroup>
		</FormControl>
	);
};

Chips.propTypes = {
	color: PropTypes.oneOf(['primary', 'secondary']),
	variant: PropTypes.oneOf(['outlined']),
	fullWidth: PropTypes.bool,
	multiple: PropTypes.bool,
	hiddenlabel: PropTypes.bool,
	appliedValue: PropTypes.arrayOf(PropTypes.string).isRequired,
	updateAppliedValue: PropTypes.func.isRequired,
	conditional: PropTypes.bool,
	disabled: PropTypes.bool.isRequired,
	label: PropTypes.string.isRequired,
	values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired
};

export default Chips;
